import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

interface ArticleMeta {
  title: string;
  description?: string;
  image: string;
  datePublished: string; // ISO 8601
  dateModified?: string;
  author?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOProps {
  page: "home" | "aboutUs" | "team" | "smileGallery" | "thankYou" | "dentalImplants" | "allOn4" | "allOnX" | "smileDesign" | "fullMouth" | "veneers" | "crowns" | "rootCanal" | "implantBridge" | "teethWhitening" | "blog";
  article?: ArticleMeta;
  faqs?: FAQItem[];
}

const SEO = ({ page, article, faqs }: SEOProps) => {
  const { t, i18n } = useTranslation();

  const baseTitle = t(`seo.${page}.title`);
  const baseDescription = t(`seo.${page}.description`);
  const keywords = t(`seo.${page}.keywords`);
  const title = article?.title || baseTitle;
  const description = article?.description || baseDescription;
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const baseUrl = "https://lsdentalcostarica.com";
  const currentUrl = `${baseUrl}${currentPath}`;

  const enUrl = currentUrl;
  const esUrl = currentUrl;

  const isArticle = !!article;

  // Primary schema: Article for blog posts, Dentist for everything else.
  const dentistSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": baseUrl,
    "name": "LS Dental Costa Rica",
    "alternateName": "LS Dental",
    "description": "Premier dental clinic in Costa Rica specializing in dental implants, All-on-4, veneers, and full mouth restoration. Save up to 70% on dental care.",
    "url": baseUrl,
    "telephone": "+1-833-648-4745",
    "email": "info@lsdentalcostarica.com",
    "image": `${baseUrl}/og-image.jpg`,
    "logo": `${baseUrl}/logo.png`,
    "priceRange": "$$",
    "currenciesAccepted": "USD, CRC",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Playas del Coco",
      "addressLocality": "Playas del Coco",
      "addressRegion": "Guanacaste",
      "postalCode": "50503",
      "addressCountry": "CR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "10.550932",
      "longitude": "-85.696820"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/lsdentalcostarica",
      "https://www.instagram.com/lsdentalcostarica",
      "https://www.youtube.com/@lsdentalcliniccostarica1928"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dental Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dental Implants", "description": "Permanent tooth replacement solutions using cutting-edge digital technology" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "All-on-4 Dental Implants", "description": "Complete arch restoration with just four implants for a full set of teeth" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dental Veneers", "description": "Ultra-thin porcelain shells to create a flawless, natural-looking smile" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Full Mouth Restoration", "description": "Comprehensive treatment to restore function and aesthetics of your entire smile" } }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const articleSchema = article && {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "image": article.image.startsWith("http") ? article.image : `${baseUrl}${article.image}`,
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Organization",
      "name": article.author || "LS Dental Costa Rica",
      "url": baseUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "LS Dental Costa Rica",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "description": description
  };

  const faqSchema = faqs && faqs.length > 0 && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.answer
      }
    }))
  };

  const primarySchema = isArticle ? articleSchema : dentistSchema;

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={article?.image && article.image.startsWith("http") ? article.image : `${baseUrl}${article?.image || "/og-image.jpg"}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={i18n.language === 'es' ? 'es_ES' : 'en_US'} />
      <meta property="og:site_name" content="LS Dental Costa Rica" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.jpg`} />

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={currentUrl} />

      <script type="application/ld+json">{JSON.stringify(primarySchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
